import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PaidIcon from "@mui/icons-material/Paid";
import StarIcon from "@mui/icons-material/Star";
import { Box, Button, Chip, Divider, Grid, Icon, IconButton, Modal, Stack, Switch, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useState } from "react";
import { ModalConfirmHiring } from "./ModalConfirmHiring";

import { useFireInsuranceQuotation } from "../../hooks/useFireInsuranceQuotation";

interface ModalApprovedV2Props {
	open: boolean;
	onClose: () => void;
	onSuccess: (plan: any, includeFireInsurance: boolean) => void;
	onSave?: (plan: any, includeFireInsurance: boolean) => void;
	plans?: any[];
	rentAmount?: number;
	chargesAmount?: number;
	estateId?: string;
	clientId?: string;
	zipCode?: string;
	propertyType?: string;
}

interface Plan {
	id: number;
	name: string;
	price: number;
	priceTotal: number;
	installments: number;
	isPopular?: boolean;
	taxa: number;
	lmiMultiplier: number;
	lmiValue: number;
	features: string[];
	originalPlan?: any;
	flagRateCharge?: string;
	bonusAmount?: string;
}

export const ModalApprovedV2 = ({
	open,
	onClose,
	onSuccess,
	onSave,
	plans,
	rentAmount,
	chargesAmount = 0,
	estateId,
	clientId,
	zipCode,
	propertyType,
}: ModalApprovedV2Props) => {
	const theme = useTheme();
	const smDown = useMediaQuery(theme.breakpoints.down("sm"));

	const {
		quoteValue: fireQuoteValue,
		isAllowed: isFireInsuranceAllowed,
		hasFireModule,
	} = useFireInsuranceQuotation({
		realEstateId: estateId,
		clientId: clientId,
		rentValue: rentAmount,
		propertyType: propertyType,
		zipCode: zipCode,
		purpose: 1, // Default to Residential
		enabled: open,
	});

	const activePlans = (plans || []).filter((p: any) => {
		const status = String(p?.status ?? p?.plan?.status ?? "").toLowerCase();
		return status === "active";
	});

	const dataPlans =
		activePlans.length > 0
			? activePlans.map((p: any) => {
					const taxa = parseFloat(p.residentialFee || "0");
					const lmiMult = parseFloat(p.lmi || "0");
					const flagRateCharge = p.flagRateCharge;

					// Calculate base amount: Rent only OR Rent + Charges
					const baseAmount = flagRateCharge === "1" ? (rentAmount || 0) + chargesAmount : rentAmount || 0;

					const price = baseAmount * (taxa / 100);
					const lmiValue = baseAmount * lmiMult;

					return {
						id: p.planId || p.id,
						name: p.plan?.description,
						price: price,
						priceTotal: price * 12, // Assuming 12 months
						installments: 12,
						isPopular: p.plan?.name?.toLowerCase().includes("gold"), // Heuristic
						taxa: taxa,
						lmiMultiplier: lmiMult,
						lmiValue: lmiValue,
						features: ["Sem fiador", "Assessoria Jurídica", "100% Digital"], // Default features
						originalPlan: p, // Keep original object for callbacks
						flagRateCharge: flagRateCharge,
						bonusAmount: p.bonusAmount,
						status: p.status,
					};
				})
			: [];

	const [confirmOpen, setConfirmOpen] = useState(false);
	const [selectedPlanForConfirm, setSelectedPlanForConfirm] = useState<any>(null);

	const handleContractClick = (plan: any, includeFireInsurance: boolean) => {
		if (includeFireInsurance) {
			setSelectedPlanForConfirm(plan);
			setConfirmOpen(true);
		} else {
			onSuccess(plan.originalPlan || plan, includeFireInsurance);
		}
	};

	const handleConfirmContract = () => {
		if (selectedPlanForConfirm) {
			onSuccess(selectedPlanForConfirm.originalPlan || selectedPlanForConfirm, true);
		}
		setConfirmOpen(false);
	};

	const formattedFireValue = fireQuoteValue ? fireQuoteValue.toLocaleString("pt-br", { style: "currency", currency: "BRL" }) : "R$ 29,90";

	const PlanCard = ({ plan }: { plan: Plan }) => {
		const [includeFireInsurance, setIncludeFireInsurance] = useState(false);

		return (
			<Box
				sx={{
					bgcolor: "#fff",
					borderRadius: 4,
					boxShadow: "rgba(17, 17, 26, 0.1) 0px 4px 16px, rgba(17, 17, 26, 0.1) 0px 8px 24px, rgba(17, 17, 26, 0.1) 0px 16px 56px;",
					overflow: "hidden",
					border: "1px solid #f0f0f0",
					mb: 3,
					display: "flex",
					flexDirection: "column",
				}}
			>
				{/* Main Card Content */}
				<Grid container sx={{ flex: 1 }}>
					{/* Left Section: Title & Price */}
					<Grid
						item
						xs={12}
						md={3}
						sx={{
							p: 2,
							background: "linear-gradient(135deg, #64B5F6 0%, #9C27B0 100%)",
							color: "#fff",
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							position: "relative",
						}}
					>
						<Stack spacing={1} alignItems="flex-start">
							{plan.isPopular && (
								<Chip
									icon={<StarIcon sx={{ fontSize: "16px !important", color: "#fff !important" }} />}
									label="Popular"
									size="small"
									sx={{
										bgcolor: "rgba(255, 255, 255, 0.2)",
										color: "#fff",
										fontWeight: 600,
										fontSize: "0.7rem",
										height: 22,
										backdropFilter: "blur(4px)",
										mb: 0.5,
									}}
								/>
							)}

							{plan.bonusAmount && parseFloat(plan.bonusAmount) > 0 && (
								<Chip
									icon={<PaidIcon sx={{ fontSize: "16px !important", color: "#7B1FA2 !important" }} />}
									label={`Bônus: ${parseFloat(plan.bonusAmount).toLocaleString("pt-br", { style: "currency", currency: "BRL" })}`}
									size="small"
									sx={{
										bgcolor: "#fff",
										color: "#7B1FA2",
										fontWeight: 700,
										fontSize: "0.75rem",
										height: 24,
										mb: 0.5,
										boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
									}}
								/>
							)}

							<Typography variant="h4" sx={{ color: "#fff", fontWeight: 800, fontSize: "1.6rem", textShadow: "0px 2px 4px rgba(0,0,0,0.1)" }}>
								{plan.name}
							</Typography>
							<Box>
								<Typography component="span" sx={{ color: "#fff", fontWeight: 700, fontSize: "1.5rem" }}>
									{plan.price.toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
								</Typography>
								<Typography component="span" sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.9rem", ml: 0.5 }}>
									/mês
								</Typography>
							</Box>

							<Box
								sx={{
									bgcolor: "rgba(0, 0, 0, 0.15)",
									px: 1.5,
									py: 0.5,
									borderRadius: 50,
									display: "inline-block",
									mt: 1,
								}}
							>
								<Typography variant="caption" sx={{ color: "#fff", fontWeight: 500 }}>
									Total Anual: {plan.priceTotal.toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
								</Typography>
							</Box>

							<Typography
								variant="caption"
								sx={{
									textDecoration: "underline",
									cursor: "pointer",
									color: "rgba(255,255,255,0.8)",
									fontWeight: 500,
									mt: 2,
									"&:hover": { color: "#fff" },
								}}
							>
								Ver detalhes do plano
							</Typography>
						</Stack>
					</Grid>

					{/* Middle Section: Technical Data */}
					<Grid item xs={12} md={6} sx={{ p: 2 }}>
						<Stack spacing={1.5}>
							<Box
								sx={{
									bgcolor: "#F8F9FE",
									borderRadius: 3,
									p: 1.5,
								}}
							>
								<Stack
									direction="row"
									divider={<Divider orientation="vertical" flexItem sx={{ borderColor: "#DDE3F2" }} />}
									justifyContent="space-around"
									alignItems="center"
									spacing={2}
								>
									{/* Taxa Garantti */}
									<Stack alignItems="center" spacing={0.5}>
										<Icon
											sx={{
												background: "linear-gradient(135deg, #64B5F6 0%, #9C27B0 100%)",
												WebkitBackgroundClip: "text",
												WebkitTextFillColor: "transparent",
												fontSize: 32,
												mb: 0.5,
											}}
										>
											percent
										</Icon>
										<Typography variant="caption" color="text.secondary" fontWeight={500}>
											Taxa Garantti
										</Typography>
										<Typography variant="subtitle1" fontWeight={800} color="#344767">
											{plan.taxa.toFixed(2)}%
										</Typography>
									</Stack>

									{/* LMI */}
									<Stack alignItems="center" spacing={0.5}>
										<Icon
											sx={{
												background: "linear-gradient(135deg, #64B5F6 0%, #9C27B0 100%)",
												WebkitBackgroundClip: "text",
												WebkitTextFillColor: "transparent",
												fontSize: 32,
												mb: 0.5,
											}}
										>
											home
										</Icon>
										<Typography variant="caption" color="text.secondary" fontWeight={500}>
											LMI
										</Typography>
										<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
											<Typography variant="subtitle1" fontWeight={800} color="#344767" sx={{ lineHeight: 1.2 }}>
												{plan.lmiMultiplier}x
											</Typography>
											<Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
												({plan.flagRateCharge === "1" ? "Aluguel + Encargos" : "Aluguel"})
											</Typography>
										</Box>
									</Stack>

									{/* Valor LMI */}
									<Stack alignItems="center" spacing={0.5}>
										<Icon
											sx={{
												background: "linear-gradient(135deg, #64B5F6 0%, #9C27B0 100%)",
												WebkitBackgroundClip: "text",
												WebkitTextFillColor: "transparent",
												fontSize: 32,
												mb: 0.5,
											}}
										>
											payments
										</Icon>
										<Typography variant="caption" color="text.secondary" fontWeight={500}>
											Valor LMI
										</Typography>
										<Typography variant="subtitle1" fontWeight={800} color="#344767">
											{plan.lmiValue.toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
										</Typography>
									</Stack>
								</Stack>
							</Box>

							<Divider />

							<Stack direction="row" spacing={3}>
								{plan.features.map((feature, index) => (
									<Stack key={index} direction="row" spacing={0.5} alignItems="center">
										<CheckIcon sx={{ color: "#4caf50" }} fontSize="small" />
										<Typography variant="body2" fontWeight="500">
											{feature}
										</Typography>
									</Stack>
								))}
							</Stack>
						</Stack>
					</Grid>
					{/* Right Section: Action */}
					<Grid
						item
						xs={12}
						md={3}
						sx={{
							p: 2,
							borderLeft: { md: "1px solid #f0f0f0" },
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Button
							variant="contained"
							color="primary"
							size="large"
							endIcon={<ArrowForwardIcon />}
							onClick={() => handleContractClick(plan, includeFireInsurance)}
							sx={{
								borderRadius: 50,
								px: 2.5,
								py: 1,
								fontWeight: 700,
								width: "100%",
								fontSize: "1.3rem",
							}}
						>
							CONTRATAR
						</Button>
					</Grid>
				</Grid>

				{/* Footer: Fire Insurance & Save */}
				<Box
					sx={{
						bgcolor: includeFireInsurance ? "#e7eaebff" : "#e7eaebff",
						p: 1.5,
						borderTop: "1px solid #eee",
						display: "flex",
						alignItems: "center",
						justifyContent: "flex-end",
						transition: "background-color 0.3s ease",
					}}
				>
					<Stack
						direction="row"
						alignItems="center"
						spacing={2}
						onClick={() => {
							if (isFireInsuranceAllowed !== false) {
								setIncludeFireInsurance(!includeFireInsurance);
							}
						}}
						sx={{ cursor: isFireInsuranceAllowed !== false ? "pointer" : "default", opacity: isFireInsuranceAllowed !== false ? 1 : 0.5 }}
					>
						{isFireInsuranceAllowed !== false && (
							<Switch checked={includeFireInsurance} onChange={(e) => setIncludeFireInsurance(e.target.checked)} color="success" />
						)}
						<Box>
							{/* aqui vamos colocar depois os detalhes do seguro incendio */}
							{/* <Typography variant="subtitle2" fontWeight="700" color={includeFireInsurance ? "success.main" : "text.primary"}>
                                {isFireInsuranceAllowed !== false
                                    ? `Adicionar Seguro Incêndio (+ ${formattedFireValue}/mês)`
                                    : "Seguro Incêndio Indisponível"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {isFireInsuranceAllowed !== false
                                    ? "Proteção essencial para o imóvel."
                                    : "Não foi possível cotar para este imóvel."}
                            </Typography> */}
						</Box>
					</Stack>
				</Box>
			</Box>
		);
	};

	return (
		<>
			<Modal open={open} onClose={onClose}>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						bgcolor: "#FFFF",
						boxShadow: 24,
						width: "95%",
						maxWidth: 1000,
						maxHeight: "90vh",
						borderRadius: 3,
						outline: "none",
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
					}}
				>
					<Box sx={{ p: 3, pb: 0, flexShrink: 0 }}>
						<Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
							<Typography variant="h4" sx={{ color: "#38aa2eff", fontWeight: 300 }}>
								Planos Aprovados
							</Typography>
							<IconButton onClick={onClose} size="small">
								<CloseIcon />
							</IconButton>
						</Grid>
					</Box>

					<Box sx={{ p: 3, pt: 0, flex: 1, overflowY: "auto" }}>
						{dataPlans.map((plan: any) => (
							<PlanCard key={plan.id} plan={plan} />
						))}
					</Box>
				</Box>
			</Modal>
			<ModalConfirmHiring
				open={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				onConfirm={handleConfirmContract}
				planName={selectedPlanForConfirm?.name || ""}
				insuranceValue={formattedFireValue}
			/>
		</>
	);
};
